import { Badge, Card } from "../../../../shared";
import { formatDate } from "../../../../utils";

function getOwnerContact(customer = {}) {
  const contacts = Array.isArray(customer?.contacts) ? customer.contacts : [];

  return (
    contacts.find((contact) => contact?.accountHolder) ||
    contacts.find((contact) => contact?.authority === "owner") ||
    contacts[0]
  );
}

function formatActivityDate(value) {
  if (!value) {
    return "Pending";
  }

  return formatDate(value, false, false, true) || "Pending";
}

function buildActivity(customer = {}, onboardingStatus) {
  const ownerContact = getOwnerContact(customer);
  const items = [
    {
      id: "customer-created",
      title: "Customer record created",
      description: "The tenant shell was created and is ready for onboarding.",
      date: customer?.dateCreated,
      color: "green",
      status: "Completed",
    },
  ];

  if (ownerContact) {
    items.push({
      id: "invite-sent",
      title: "Owner invite sent",
      description: ownerContact?.email
        ? `An onboarding invite was prepared for ${ownerContact.email}.`
        : "An onboarding invite was prepared for the owner account.",
      date: customer?.metadata?.lastInviteSentAt || ownerContact?.dateCreated,
      color: customer?.metadata?.lastInviteSentAt || ownerContact?.dateCreated ? "green" : "yellow",
      status: customer?.metadata?.lastInviteSentAt || ownerContact?.dateCreated ? "Completed" : "Pending",
    });
  }

  items.push({
    id: "owner-activated",
    title: "Owner account activated",
    description: ownerContact?.active
      ? "The primary owner finished account setup and can sign in."
      : "The primary owner still needs to activate the invite.",
    date: ownerContact?.active ? ownerContact?.dateUpdated || ownerContact?.dateCreated : null,
    color: ownerContact?.active ? "green" : "yellow",
    status: ownerContact?.active ? "Completed" : "Pending",
  });

  items.push({
    id: "company-activated",
    title: "Company setup completed",
    description:
      customer?.metadata?.onboarded || customer?.active
        ? "The company profile, address, and onboarding setup were completed."
        : "The company still needs to finish the onboarding review step.",
    date: customer?.metadata?.onboardedAt || null,
    color: customer?.metadata?.onboarded || customer?.active ? "green" : "yellow",
    status: customer?.metadata?.onboarded || customer?.active ? "Completed" : "Pending",
  });

  return {
    ownerContact,
    items,
    summary:
      onboardingStatus?.description ||
      "No onboarding activity is available for this customer yet.",
  };
}

const ActivityRow = ({ item, isLast }) => (
  <div className="relative pl-8">
    <div className="absolute left-0 top-1.5 h-3 w-3 rounded-full bg-primary-500" />
    {!isLast ? (
      <div className="absolute left-[5px] top-5 h-[calc(100%-8px)] w-px bg-gray-200" />
    ) : null}
    <div className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="font-medium text-gray-900">{item.title}</p>
          <p className="text-sm text-gray-500 mt-1">{item.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge color={item.color} label={item.status} />
          <span className="text-xs text-gray-500">{formatActivityDate(item.date)}</span>
        </div>
      </div>
    </div>
  </div>
);

const ActivityTab = ({ customer, onboardingStatus }) => {
  const activity = buildActivity(customer, onboardingStatus);

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h3 className="text-lg font-medium">Onboarding Activity</h3>
            <p className="text-sm text-gray-500 mt-1">{activity.summary}</p>
          </div>
          <Badge color={onboardingStatus?.color} label={onboardingStatus?.label} />
        </div>

        <div className="mt-6 space-y-4">
          {activity.items.map((item, index) => (
            <ActivityRow
              key={item.id}
              item={item}
              isLast={index === activity.items.length - 1}
            />
          ))}
        </div>
      </Card>
    </div>
  );
};

export default ActivityTab;
